import React, {
    ComponentProps,
    FC,
    useCallback,
    useContext,
    useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';
import Loading from 'components/Loading';
import Chart from 'components/ChartContainer/components/Chart/Chart';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import BudgetPivotGrid from 'routes/BudgetModule/components/shared/BudgetChart/BudgetPivotGrid/BudgetPivotGrid';
import DataLocked from 'assets/data_locked.svg?react';
import ChartMessage from 'components/ChartContainer/components/Chart/ChartMessage';
// TODO: put this css in useStyles
import 'routes/Dashboard/components/GridChart/_gridChart.scss';
import {
    manageUnknownCellValue,
    stringifyJsonFromResponseData,
} from 'helpers/formatHelper';
import { PivotFieldsAggridT } from 'business/definitions';
import { cleanQueryResult } from 'helpers/chartHelper';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { ChartProps } from 'components/ChartContainer/components/Chart/types';
import { ChartContext } from 'components/ChartContainer/context/ChartContext';
import { snakify } from 'helpers/syntaxHelper';
import PermissionErrorText from 'ui/PermissionErrorText/PermissionErrorText';
import { useMutateArgumentPivotGridSettings } from './hooks/ArgumentPivotGridSettings';
import BudgetProjectsPivotGrid from '../../../Analyse/components/BudgetProjectsPivotGrid';
import { CurrentBudgetProposalContext } from 'routes/BudgetModule/components/BudgetProposal/Resource/contexts/CurrentBudgetProposalContext';
import { HttpError, RQAPICallWithData } from 'store/APIClient';
import { filtersPostProcessingMiddleware } from 'routes/BudgetModule/components/shared/Filters/filtersMiddlewares';
import {
    ArgumentIndicatorNameT,
    GetLayoutChartDataResponse,
} from 'components/ChartContainer/types';
import NoDataState from 'ui/NoDataState/NoDataState';

const nullFct = () => {};

const vizMapBudget: Record<ArgumentIndicatorNameT, FC> = {
    projects_with_budget_inscriptions: BudgetProjectsPivotGrid,
    budget_analysis: BudgetPivotGrid,
    undefined: BudgetPivotGrid,
};

const DEBOUNCE_TIME = 2000;

type Props = {
    chartKey: number;
    setExporterToExcel?: null | ((arg1: FlowAnyFunction) => void);
};

export function ArgumentChartSelector({
    chartKey,
    setExporterToExcel: setExporterToExcelProp = null,
}: Props): React.ReactElement {
    // using isFetching and not isLoading, to avoid flicker effect on settings
    // when going back to a already visited argument
    const {
        data,
        isFetching,
        isError,
        error: requestError,
    } = useQuery<GetLayoutChartDataResponse, HttpError>({
        queryKey: [`chart${chartKey}`],
        queryFn: async () => {
            const response =
                await RQAPICallWithData<GetLayoutChartDataResponse>({
                    endpoint: `explore/layouts/charts/argument/${chartKey}`,
                    method: 'GET',
                    postProcessingCallback: filtersPostProcessingMiddleware,
                });
            return response.content.data;
        },
    });

    const budgetProposal = useContext(CurrentBudgetProposalContext);
    const selectedYear = budgetProposal.exercice;
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { mutate: mutateArgumentPivotGridSettings } =
        useMutateArgumentPivotGridSettings(chartKey);

    // we use a useCallback to make sure the debounced function stays the same,
    // if it is redefined, the debounce should not work
    const onPivotFieldsAggridUpdate = useCallback(
        _.debounce((pivotFieldsAggrid: PivotFieldsAggridT) => {
            // Once the columns have been changed, we are sending a request
            // to the back to update the settings saved there
            mutateArgumentPivotGridSettings({
                chartSettings: data?.settings ?? {},
                pivotFieldsAggrid,
            });
            // The settings cached in react query needs to be updated as well.
            // The grid component is comparing those cached settings with the one actually saved
            // in the grid. If he spots a difference between those two, he rerenders.
            // If we don't update those cache settings, the grid will always find a difference causing
            // an infinite loop.
            queryClient.setQueryData(
                [`chart${chartKey}`],
                (oldData: ChartProps) => ({
                    ...oldData,
                    settings: {
                        ...oldData.settings,
                        pivotFieldsAggrid,
                    },
                }),
            );
        }, DEBOUNCE_TIME),
        [data, mutateArgumentPivotGridSettings, chartKey],
    );

    const onInscriptionsModalOpened = useCallback(
        () =>
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetProposal.argument.penModalOpened,
                {
                    budgetProposal: budgetProposal.name,
                    phase: t(`budgetProposal.${budgetProposal.phase}`),
                },
            ),
        [t, budgetProposal?.name, budgetProposal?.phase],
    );

    const onInscriptionsModalClosed = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [`chart${chartKey}`] });
    }, [chartKey, queryClient]);

    // The filters are camelized in Argument.tsx in
    // the postProcessingCallback of the useChartDataMaker hook.
    // I am not sure why, but to avoid breaking something, I keep
    // the camelize and just snakify the filters when it is expected
    // for them to be in snake_case.
    // This is useful for the Gauge chart since the get chart max value
    // request will use the filter.
    const chartContextValue = useMemo(
        () => ({
            queryMetrics: data?.metrics ?? [],
            queryFilters:
                data?.filters.map((f: QueryFilterT) => ({
                    ...f,
                    field: snakify(f.field),
                })) ?? [],
            queryDimensions: data?.dimensions ?? [],
            publicToken: undefined,
            chartKey,
            isChartViewedOnDashboard: false,
            isUpdateChartLoading: false,
            disableDashboardFilters: false,
        }),
        [data?.metrics, data?.dimensions, data?.filters, chartKey],
    );

    if (!selectedYear || !budgetProposal || isFetching) return <Loading />;
    if (!data) return <NoDataState />;
    if (isError) {
        if ([401, 403].includes(requestError.status)) {
            return (
                <ChartMessage>
                    <DataLocked />
                    <PermissionErrorText />
                </ChartMessage>
            );
        }
        return <Typography>{t('asyncComponent.error')}</Typography>;
    }

    const { data: resData, columns: resColumns } = cleanQueryResult({
        columns: data.queryResult.columns,
        data: data.queryResult.data
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((row: any) => manageUnknownCellValue(row, t('cellar.blanks')))
            .map(stringifyJsonFromResponseData),
        queryMetrics: data.metrics,
        queryDimensions: data.dimensions,
    });
    // This is a trick to inject the tracker logic in the export function
    // without changing the generic child component.
    // Since the state is a function and that setState accepts a function to set the state,
    // we are forced to use a function that returns a function as an argument.
    // We should avoid having functions as state.
    const setExporterToExcel = (getExporter: () => () => void) => {
        if (!setExporterToExcelProp) return;

        setExporterToExcelProp(() => () => {
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetProposal.argument.pivotGridExport,
                {
                    budgetProposal: budgetProposal.name,
                    phase: t(`budgetProposal.${budgetProposal.phase}`),
                },
            );
            getExporter()();
        });
    };

    // When we have a pivot_table coming from decision, pivot_table vizType is a regular chart and not
    // a budget pivot grid
    const isRegularPivotTable =
        data.vizType === 'pivot_table' &&
        _(data.settings).has('pivotFieldsAggrid') &&
        data.settings.pivotTableExportedFromDecisionToBudget;

    let ChartType: FC<
        | ComponentProps<typeof Chart>
        | ComponentProps<typeof BudgetPivotGrid>
        | ComponentProps<typeof BudgetProjectsPivotGrid>
    > = Chart;

    if (
        !isRegularPivotTable &&
        data.vizType === 'pivot_table' &&
        data.customData
    ) {
        ChartType = vizMapBudget[data.customData.indicatorName];
    }

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexFlow: 'column',
            }}
            id={`grid-chart-${chartKey}`}
        >
            <ErrorBoundary>
                <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
                    {
                        // The ChartContext is used to pass props all the way to the actual chart implementation.
                        // This is necessary for the max value of Gauge chart to work.
                    }
                    <ChartContext.Provider value={chartContextValue}>
                        <ChartType
                            data={resData}
                            columns={resColumns}
                            chartKey={chartKey}
                            vizType={data.vizType}
                            applyChartSettings={nullFct}
                            queryMetrics={data.metrics}
                            queryDimensions={data.dimensions}
                            queryFilters={data.filters}
                            gridFilters={data.settings.gridFilters}
                            settings={data.settings}
                            selectedYear={selectedYear}
                            showDetailsModal={nullFct}
                            pivotFieldsAggrid={data.settings.pivotFieldsAggrid}
                            isLoading={false}
                            budgetProposal={budgetProposal}
                            setExporterToExcel={setExporterToExcel}
                            onPivotFieldsAggridUpdate={
                                onPivotFieldsAggridUpdate
                            }
                            onInscriptionsModalOpened={
                                onInscriptionsModalOpened
                            }
                            onInscriptionsModalClosed={
                                onInscriptionsModalClosed
                            }
                            baseFilters={data.filters}
                        />
                    </ChartContext.Provider>
                </Box>
            </ErrorBoundary>
        </Box>
    );
}
